import type React from 'react';
import { useState } from 'react';
import { IonIcon } from '@ionic/react';
import { exitOutline } from 'ionicons/icons';
import { RoleChip } from '../../components/RoleChip';
import { toFullRoleLabel } from '../../components/roleLabels';
import { FantasyScore } from '../../components/FantasyScore';
import { ButtonsDropdown, type ButtonsDropdownAction } from '../../components/ui/ButtonsDropdown';
import { cn } from '../../../lib/utils';
import type { Player } from '../../../types/graphql-generated';
import { PlayerAvatar } from '../../components/PlayerAvatar';
import { ExtendedPlayerInfoModal } from '../../components/ExtendedPlayerInfoModal';
import { useReferenceCompetitionId } from '../../../contexts/ReferenceCompetitionContext';
import { isPlayerInCompetition } from '../../../utils/playerCompetition';

interface PlayerCardProps {
  player: Player;

  // Optional week-specific score display
  weekNumber?: number;
  weekFantasyScore?: number;

  // Compact mode: single-line stats and inline text actions (used in TeamTab)
  compactMode?: boolean;

  // extensions
  rightColumn?: React.ReactNode;
  pvp?: number;
  releaseClauseDisplay?: React.ReactNode;
  bottomContent?: React.ReactNode;
  footer?: React.ReactNode;
  footerDetails?: React.ReactNode;
  footerActionDetails?: React.ReactNode;
  footerActions?: ButtonsDropdownAction[];

  // styles
  className?: string;
}

const STAT_VALUE_CLASS = 'font-heading text-[14px] font-extrabold leading-none text-[var(--fc-navy)]';
const INLINE_LABEL_CLASS = 'font-bold text-[var(--fc-ink-3)]';
const INLINE_VALUE_CLASS = 'font-heading font-extrabold text-[var(--fc-navy)]';

/** One segment of the stat strip: a small uppercase label above its value. */
const StatCell = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="flex min-w-0 flex-col gap-1.5 border-l border-slate-200/70 px-2.5 py-2 first:border-l-0">
    <span className="truncate text-[9px] font-extrabold uppercase tracking-[0.03em] text-[var(--fc-ink-3)]">
      {label}
    </span>
    <div className="min-w-0">{children}</div>
  </div>
);

const InlineSeparator = () => <span className="font-extrabold text-slate-300">·</span>;

export const PlayerCard = ({
  player,
  weekNumber,
  weekFantasyScore,
  compactMode = false,
  pvp,
  releaseClauseDisplay,
  bottomContent,
  footer,
  footerDetails,
  footerActionDetails,
  footerActions,
  className
}: PlayerCardProps) => {
  const [showExtendedInfo, setShowExtendedInfo] = useState(false);
  const referenceCompetitionId = useReferenceCompetitionId();
  // A player without a team, or whose team plays in a different competition,
  // is shown as "No está en la liga" instead of their team and stats.
  const isInLeague = isPlayerInCompetition(player.team, referenceCompetitionId);

  // Week score: label falls back to "Ult. jornada" (full) / "Últ:" (compact)
  // when we don't have the match-week number, and the value falls back to "-"
  // when there is no score to show.
  const displayWeekNumber = weekNumber !== undefined ? weekNumber : player.lastWeekNumber;
  const displayScore = weekFantasyScore !== undefined ? weekFantasyScore : player.lastWeekScore;
  const hasWeekScore = displayScore !== null && displayScore !== undefined;
  const hasWeekNumber = displayWeekNumber !== null && displayWeekNumber !== undefined;
  const weekLabel = hasWeekNumber ? `Jornada ${displayWeekNumber}` : 'Ult. jornada';
  const weekLabelShort = hasWeekNumber ? `J${displayWeekNumber}:` : 'Últ:';

  const totalText =
    player.totalScore !== null && player.totalScore !== undefined ? player.totalScore.toFixed(1) : '-';
  const marketValueText =
    player.marketValue !== null && player.marketValue !== undefined
      ? `${player.marketValue.toLocaleString('es-ES')}€`
      : '-';
  const hasPvp = pvp !== null && pvp !== undefined;

  // The clause segment only appears when the caller supplies clause info (e.g.
  // in the team views). Elsewhere (market) the strip drops to three segments
  // instead of showing an empty column.
  const showClause = releaseClauseDisplay !== null && releaseClauseDisplay !== undefined;

  const teamName = player.team?.nameShow || player.team?.shortName || '';
  const roleFull = player.role ? toFullRoleLabel(player.role) : '';
  const subtitle = [roleFull, teamName].filter((part) => part && part !== '-').join(' · ');

  const hasStructuredFooter = !!footerDetails || !!footerActions?.length;
  const shouldRenderFooter = !!footer || hasStructuredFooter;

  const notInLeagueNote = (
    <div className="flex items-center gap-1 text-red-600">
      <IonIcon icon={exitOutline} className="text-lg" />
      <span className="text-xs font-medium">No está en la liga</span>
    </div>
  );

  // Compact stats: a single line beneath the name, e.g.
  // "T: 28.3 · J3: [17.5] · VM: 605€ · C: 875€"
  const compactStatLine = (
    <div className="mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-[12.5px] leading-tight">
      <span className={INLINE_LABEL_CLASS}>T:</span>
      <span className={cn(INLINE_VALUE_CLASS, totalText === '-' && 'text-[var(--fc-ink-3)]')}>{totalText}</span>
      <InlineSeparator />
      <span className={INLINE_LABEL_CLASS}>{weekLabelShort}</span>
      {hasWeekScore ? (
        <FantasyScore value={displayScore as number} />
      ) : (
        <span className={cn(INLINE_VALUE_CLASS, 'text-[var(--fc-ink-3)]')}>-</span>
      )}
      <InlineSeparator />
      <span className={INLINE_LABEL_CLASS}>VM:</span>
      <span className={cn(INLINE_VALUE_CLASS, marketValueText === '-' && 'text-[var(--fc-ink-3)]')}>{marketValueText}</span>
      {showClause && (
        <>
          <InlineSeparator />
          <span className={INLINE_LABEL_CLASS}>C:</span>
          <span className={INLINE_VALUE_CLASS}>{releaseClauseDisplay}</span>
        </>
      )}
    </div>
  );

  return (
    <>
      <div
        data-testid={`player-card-${player.id}`}
        className={cn(
          'overflow-visible rounded-2xl border border-[#e8ebf1] bg-white text-sm shadow-[0_1px_2px_rgba(20,30,60,0.05)]',
          className
        )}
      >
        <div
          className="w-full text-left"
          onClick={() => setShowExtendedInfo(true)}
          style={{ cursor: 'pointer' }}
          role="button"
          tabIndex={0}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              setShowExtendedInfo(true);
            }
          }}
        >
          {/* Header: avatar · name line · (compact stats / subtitle) */}
          <div className={cn('flex items-center gap-3', compactMode ? 'p-2.5' : 'p-3')}>
            <div className="shrink-0">
              <PlayerAvatar image={player.image} nick={player.nick} size={compactMode ? 'compact' : 'md'} />
            </div>

            <div className="flex min-w-0 flex-1 flex-col gap-1">
              <div className="flex items-center gap-2">
                {!!player.role && <RoleChip role={player.role} />}
                {isInLeague && player.team && (
                  <div
                    className="flex h-[18px] w-[18px] shrink-0 items-center justify-center overflow-hidden rounded-full border border-[#e0e5ee] bg-white text-[8px] font-bold uppercase text-slate-600"
                    data-testid={`team-badge-${player.id}`}
                  >
                    {player.team.shield ? (
                      <img
                        src={player.team.shield}
                        alt={player.team.shortName || player.team.nameShow || 'Escudo'}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <span>{player.team.shortName || ''}</span>
                    )}
                  </div>
                )}
                <p className="min-w-0 flex-1 truncate font-heading font-bold leading-tight">{player.nick}</p>
              </div>

              {!isInLeague
                ? notInLeagueNote
                : compactMode
                  ? compactStatLine
                  : subtitle && (
                      <p className="truncate text-xs font-semibold text-[var(--fc-ink-3)]">{subtitle}</p>
                    )}
            </div>
          </div>

          {/* Full stat strip: Total · Jornada · (Cláusula) · Valor */}
          {!compactMode && isInLeague && (
            <div className={cn('grid border-t border-slate-100 bg-slate-50/70', showClause ? 'grid-cols-4' : 'grid-cols-3')}>
              <StatCell label="Total">
                <span className={cn(STAT_VALUE_CLASS, totalText === '-' && 'text-[var(--fc-ink-3)]')}>
                  {totalText}
                </span>
              </StatCell>

              <StatCell label={weekLabel}>
                {hasWeekScore ? (
                  <div className="flex">
                    <FantasyScore value={displayScore as number} />
                  </div>
                ) : (
                  <span className={cn(STAT_VALUE_CLASS, 'text-[var(--fc-ink-3)]')}>-</span>
                )}
              </StatCell>

              {showClause && (
                <StatCell label="Cláusula">
                  <div className="text-[14px] leading-none">{releaseClauseDisplay}</div>
                </StatCell>
              )}

              <StatCell label="Valor">
                <div className="flex flex-col gap-0.5">
                  <span className={cn(STAT_VALUE_CLASS, marketValueText === '-' && 'text-[var(--fc-ink-3)]')}>
                    {marketValueText}
                  </span>
                  {hasPvp && (
                    <span className="text-[10.5px] font-bold leading-none text-[var(--fc-green)]">
                      PVP {(pvp as number).toLocaleString('es-ES')}€
                    </span>
                  )}
                </div>
              </StatCell>
            </div>
          )}

          {bottomContent && <div className="px-3 pt-2">{bottomContent}</div>}
        </div>

        {shouldRenderFooter &&
          (compactMode ? (
            <div className="flex flex-wrap items-center justify-end gap-x-2 gap-y-1 border-t border-gray-100 px-2.5 py-1.5">
              {footer ?? (
                <>
                  {/* Status text (e.g. "Jugador en venta") is intentionally omitted in
                      compact mode to keep the row short. */}
                  {!!footerActions?.length && (
                    <div className="ml-auto flex flex-wrap items-center justify-end gap-x-1 gap-y-0.5">
                      {footerActions.map((action) => (
                        <button
                          key={`${action.label}-${action.testId ?? 'action'}`}
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            action.onClick(event);
                          }}
                          disabled={action.disabled}
                          data-testid={action.testId}
                          className={cn(
                            'inline-flex items-center gap-1 rounded-md px-1.5 py-1 font-heading text-xs font-bold underline decoration-1 underline-offset-2 transition-colors disabled:opacity-50',
                            action.variant === 'danger'
                              ? 'text-red-600 hover:bg-red-50'
                              : 'text-[var(--fc-navy)] hover:bg-slate-100'
                          )}
                        >
                          <IonIcon icon={action.icon} className="shrink-0 text-sm no-underline" />
                          <span>{action.label}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          ) : (
            <div className="border-t border-gray-100 px-3.5 py-2.5">
              {footer ?? (
                <div className="flex flex-wrap items-center justify-between gap-3">
                  {footerDetails && <div className="min-w-0 flex-1 text-sm text-slate-600">{footerDetails}</div>}
                  {!!footerActions?.length && (
                    <div className="ml-auto flex shrink-0 flex-col items-end gap-1.5">
                      {footerActionDetails && (
                        <div className="flex w-full justify-end text-right text-xs text-slate-500">{footerActionDetails}</div>
                      )}
                      <ButtonsDropdown actions={footerActions} />
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
      </div>

      {/* Extended Player Info Modal */}
      <ExtendedPlayerInfoModal
        isOpen={showExtendedInfo}
        onClose={() => setShowExtendedInfo(false)}
        playerId={player.id}
        referenceCompetitionId={referenceCompetitionId}
      />
    </>
  );
};
